// src/constants/fishIQPublicSources.ts
// FishIQ Public Source Registry v1
// Static metadata only. No live API calls. No user-tap fetches. No confidence scoring.

import type { FishIQPublicSourceProvider, FishIQPublicSourceRef, FishIQSpeciesSeed } from '../types';

export const FISHIQ_PUBLIC_SOURCE_REGISTRY_VERSION = 'v1' as const;

export const FISHIQ_PUBLIC_SOURCE_REGISTRY_FLAG = 'FISHIQ_PUBLIC_SOURCE_REGISTRY' as const;


export const FISHIQ_PUBLIC_SOURCE_REGISTRY: Record<string, FishIQPublicSourceRef> = {
  roddo_editorial_seed_v1: {
    id: 'roddo_editorial_seed_v1',
    provider: 'roddo_editorial',
    kind: 'editorial_seed',
    title: 'Roddo editorial seed v1',
    publisher: 'Roddo',
    registryStatus: 'active',
    notes: 'Legacy curated FishIQ seed copy. Public-source registry metadata may be layered on top, but this is not an external citation.',
  },
  roddo_editorial_seed_v2: {
    id: 'roddo_editorial_seed_v2',
    provider: 'roddo_editorial',
    kind: 'editorial_seed',
    title: 'Roddo editorial seed v2',
    publisher: 'Roddo',
    registryStatus: 'active',
    notes: 'Legacy curated FishIQ seed copy for expanded saltwater and aquatic entries. Not an external citation.',
  },
  gbif_species_occurrence_ready: {
    id: 'gbif_species_occurrence_ready',
    provider: 'gbif',
    kind: 'species_occurrence',
    title: 'GBIF species occurrence backbone',
    publisher: 'Global Biodiversity Information Facility',
    url: 'https://www.gbif.org/',
    registryStatus: 'ready',
    notes: 'Registry-ready source for future offline/imported species occurrence checks. Not called from user taps.',
  },
  noaa_fisheries_species_directory_ready: {
    id: 'noaa_fisheries_species_directory_ready',
    provider: 'noaa',
    kind: 'species_profile',
    title: 'NOAA Fisheries species directory',
    publisher: 'NOAA Fisheries',
    url: 'https://www.fisheries.noaa.gov/species-directory',
    registryStatus: 'ready',
    jurisdiction: 'United States marine and anadromous species',
    notes: 'Registry-ready source for future curated/imported marine species profiles. Not called from user taps.',
  },
  noaa_tides_currents_ready: {
    id: 'noaa_tides_currents_ready',
    provider: 'noaa',
    kind: 'weather_oceanic',
    title: 'NOAA Tides and Currents',
    publisher: 'NOAA CO-OPS',
    url: 'https://tidesandcurrents.noaa.gov/',
    registryStatus: 'ready',
    jurisdiction: 'United States coastal waters',
    notes: 'Registry-ready source for future tide/current context. Not called from FishIQ cards or map taps.',
  },
  usgs_nhd_ready: {
    id: 'usgs_nhd_ready',
    provider: 'usgs',
    kind: 'waterbody',
    title: 'USGS National Hydrography Dataset',
    publisher: 'U.S. Geological Survey',
    url: 'https://www.usgs.gov/national-hydrography',
    registryStatus: 'ready',
    jurisdiction: 'United States inland waters',
    notes: 'Registry-ready source for future waterbody truth/context imports. Not called from user taps.',
  },
  state_agency_regulations_ready: {
    id: 'state_agency_regulations_ready',
    provider: 'state_agency',
    kind: 'regulation',
    title: 'State agency fishing regulations',
    publisher: 'State fish and wildlife agencies',
    registryStatus: 'ready',
    jurisdiction: 'Varies by state/province',
    notes: 'Placeholder source family for jurisdiction-specific regulation links. Must be resolved to a real agency/source before display as a citation.',
  },
};

const cleanKey = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const hasAny = (haystack: string, needles: string[]) => needles.some((needle) => haystack.includes(needle));

const registryKeyForEditorialTitle = (title: unknown): string | null => {
  const key = cleanKey(title);
  if (key === 'roddo_editorial_seed_v1') return 'roddo_editorial_seed_v1';
  if (key === 'roddo_editorial_seed_v2') return 'roddo_editorial_seed_v2';
  return null;
};

export function getFishIQPublicSource(key: string): FishIQPublicSourceRef | null {
  return FISHIQ_PUBLIC_SOURCE_REGISTRY[key] ?? null;
}

export function getFishIQPublicSources(keys: readonly string[] | undefined): FishIQPublicSourceRef[] {
  if (!Array.isArray(keys)) return [];
  const seen = new Set<string>();
  const out: FishIQPublicSourceRef[] = [];
  for (const key of keys) {
    if (!key || seen.has(key)) continue;
    const source = getFishIQPublicSource(key);
    if (!source) continue;
    seen.add(key);
    out.push(source);
  }
  return out;
}

export function fishIQPublicSourceKeysForSpecies(species: Partial<FishIQSpeciesSeed>): string[] {
  const keys = new Set<string>();

  for (const source of species.editorialSources || []) {
    const editorialKey = registryKeyForEditorialTitle(source?.title);
    if (editorialKey) keys.add(editorialKey);
  }

  const hay = [
    species.commonName,
    species.scientificName,
    species.family,
    species.category,
    species.aquaticKind,
    ...(Array.isArray(species.habitats) ? species.habitats : []),
    ...(Array.isArray(species.regions) ? species.regions : []),
    ...(Array.isArray(species.waterRealms) ? species.waterRealms : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const category = String(species.category || '').toLowerCase();
  const isSaltish =
    category === 'saltwater' ||
    category === 'both' ||
    hasAny(hay, ['salt', 'marine', 'ocean', 'bay', 'estuary', 'inshore', 'gulf', 'coast', 'tide', 'surf']);
  const isFreshish =
    category === 'freshwater' ||
    category === 'both' ||
    hasAny(hay, ['fresh', 'lake', 'river', 'pond', 'creek', 'reservoir', 'stream']);

  if (species.scientificName && !/spp\.?/i.test(String(species.scientificName))) {
    keys.add('gbif_species_occurrence_ready');
  }

  if (isSaltish) {
    keys.add('noaa_fisheries_species_directory_ready');
    keys.add('noaa_tides_currents_ready');
  }

  if (isFreshish) {
    keys.add('usgs_nhd_ready');
  }

  // Kept as registry metadata only. Do not display as a real citation until a jurisdiction link is attached.
  keys.add('state_agency_regulations_ready');

  return Array.from(keys);
}

export function attachFishIQPublicSourceRefs<T extends FishIQSpeciesSeed>(species: T[]): T[] {
  return species.map((item) => {
    const publicSourceRefs = fishIQPublicSourceKeysForSpecies(item);
    return {
      ...item,
      publicSourceRefs,
      sourceRegistryVersion: FISHIQ_PUBLIC_SOURCE_REGISTRY_VERSION,
    } as T;
  });
}

export function isFishIQSourceProvider(value: unknown): value is FishIQPublicSourceProvider {
  return [
    'roddo_editorial',
    'gbif',
    'noaa',
    'state_agency',
    'usgs',
    'nps',
    'fws',
    'other_public',
  ].includes(String(value));
}
